import { useId } from 'react'
import { CircleAlert, History, Info, LoaderCircle } from 'lucide-react'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { historyLabel, historySpan, historyState, type HistoryTone } from './detailStats'
import type { LocalDate } from './recurringRules'
import type { Bill, Period } from './recurringWire'
import { useHitTarget } from '../../platform/responsive/hitTarget'

export const NO_HISTORY = 'No payments recorded yet'
export const TRUNCATED_NOTE = 'Only the most recent payments are shown.'

/** Paid → what was actually paid (P5); anything else → what the bill expects. */
function amountOf(p: Period, bill: Bill): number {
  return p.status === 'paid' ? (p.payment?.amountCents ?? bill.expectedCents) : bill.expectedCents
}

type Row = { period: Period; label: string; state: { tone: HistoryTone; text: string }; cents: number }

/**
 * The state's word, with a dot that repeats it. The dot is decorative: the
 * word carries the meaning (rule 11, R12), so "Paid late" is legible in
 * greyscale and to a screen reader.
 */
function Lead({ row }: { row: Row }) {
  return (
    <span className="rec-hist__lead">
      <span className={`rec-hist__dot rec-hist__dot--${row.state.tone}`} aria-hidden="true" />
      <span className="rec-hist__label">{row.label}</span>
    </span>
  )
}

const State = ({ row }: { row: Row }) => <span className={`rec-hist__state rec-hist__state--${row.state.tone}`}>{row.state.text}</span>

const Amount = ({ row, currency }: { row: Row; currency: string }) => (
  <span className="rec-hist__amount">
    <Money cents={row.cents} currency={currency} />
  </span>
)

/** "Find past payments" as the history needs it: the screen owns the request. */
export type FindPast = {
  onFind: () => void
  busy: boolean
  /** The last outcome: a count sentence, or why it could not run. */
  outcome: { tone: 'done' | 'problem'; text: string } | null
}

/** NEW COPY (owner review): the in-flight status. */
export const FINDING = 'Looking for past payments…'

/**
 * "Payment history" (M7-R11, R12, R26): one row per timeline period, newest
 * first. A `DataGrid` from 640px up; a `<ul>` of stacked cards on a phone.
 * Presentational: the timeline arrives as props.
 *
 * **Every row opens its period** (Task 10): on the grid the period's label is
 * a `<button>` whose hit area is stretched over the whole row (the row keeps
 * `role="row"` — a table row cannot itself be a button); on a phone the whole
 * card is the button. Both are named by the period and its state.
 *
 * `findPast` adds "Find past payments" to the heading row, with its outcome on
 * the line below; absent (a bill with no account) there is no button at all.
 */
export function PaymentHistory({
  bill,
  periods,
  truncated,
  today,
  phone,
  onOpen,
  findPast,
}: {
  bill: Bill
  periods: Period[]
  truncated: boolean
  today: LocalDate
  phone: boolean
  onOpen: (p: Period) => void
  findPast?: FindPast
}) {
  const headingId = useId()
  // The period button's own box meets the floor, not only its stretched
  // ::after: the sweep measures the control, and a 17px button is what it saw.
  const openFloor = useHitTarget('page')
  const rows: Row[] = [...periods]
    .sort((a, b) => (a.dueDate < b.dueDate ? 1 : a.dueDate > b.dueDate ? -1 : 0))
    .map((p) => ({ period: p, label: historyLabel(p, bill.frequency), state: historyState(p, today, bill.isActive ? bill.nextDue : null), cents: amountOf(p, bill) }))
  const columns: Column<Row>[] = [
    {
      key: 'period',
      header: 'Period',
      width: 'minmax(0, 1fr)',
      render: (r) => (
        <button type="button" className="rec-hist__open" style={{ minHeight: openFloor }} aria-haspopup="dialog" aria-label={`${r.label}, ${r.state.text}`} onClick={() => onOpen(r.period)}>
          <Lead row={r} />
        </button>
      ),
    },
    { key: 'state', header: 'Status', width: 'auto', align: 'end', render: (r) => <State row={r} /> },
    { key: 'amount', header: 'Amount', width: 'minmax(96px, auto)', align: 'end', render: (r) => <Amount row={r} currency={bill.currency} /> },
  ]
  return (
    <Panel className="rec-hist">
      <section aria-labelledby={headingId}>
        <div className="rec-hist__head">
          {/* h2: a sibling of the hero's h2, under the TopBar's h1 (Ruling 31). */}
          <h2 id={headingId} className="rec-hist__title">
            Payment history
          </h2>
          <div className="rec-hist__head-side">
            {rows.length > 0 && <span className="rec-hist__caption">Newest first · {historySpan(periods)}</span>}
            {findPast && (
              <Button
                variant="outline"
                size="panel"
                className="rec-hist__find"
                aria-disabled={findPast.busy || undefined}
                onClick={() => {
                  // aria-disabled still dispatches a click; this is the guard.
                  if (!findPast.busy) findPast.onFind()
                }}
              >
                <span className={findPast.busy ? 'spin rec-hist__find-mark' : 'rec-hist__find-mark'}>
                  <Icon glyph={findPast.busy ? LoaderCircle : History} size={14} />
                </span>
                Find past payments
              </Button>
            )}
          </div>
        </div>
        {findPast && (
          <p
            className={`rec-hist__line rec-hist__line--find${findPast.outcome?.tone === 'problem' ? ' rec-hist__line--problem' : ''}`}
            role="status"
          >
            {findPast.busy ? (
              FINDING
            ) : findPast.outcome?.tone === 'problem' ? (
              <>
                <Icon glyph={CircleAlert} size={13} />
                {findPast.outcome.text}
              </>
            ) : (
              (findPast.outcome?.text ?? '')
            )}
          </p>
        )}
        {rows.length === 0 ? (
          <p className="rec-hist__line">{NO_HISTORY}</p>
        ) : phone ? (
          <ul className="rec-hist__cards" aria-label="Payment history">
            {rows.map((r) => (
              <li key={r.period.dueDate}>
                <button type="button" className="rec-hist__card hoverable" aria-haspopup="dialog" onClick={() => onOpen(r.period)}>
                  <span className="rec-hist__card-main">
                    <Lead row={r} />
                    <State row={r} />
                  </span>
                  <Amount row={r} currency={bill.currency} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rec-hist__grid">
            <DataGrid
              columns={columns}
              groups={[{ label: '', rows }]}
              rowKey={(r) => r.period.dueDate}
              label="Payment history"
              empty={null}
              header="hidden"
            />
          </div>
        )}
        {truncated && rows.length > 0 && (
          <p className="rec-hist__line rec-hist__line--note">
            <Icon glyph={Info} size={13} />
            {TRUNCATED_NOTE}
          </p>
        )}
      </section>
    </Panel>
  )
}
